import { TWO_FACTOR_TYPE } from "@tastyworks/shared-utils/misc/constants"
import { isEmpty, isNil, last, merge, startsWith } from "lodash-es"
import type { RestResource } from "./common"
import type { DeSer, Parser, Serializer, Updater } from "./deser"
import type Logger from "./logger"
import {
  UrlPathBuilder,
  toFormData,
  type FileParams,
  type Params,
} from "./request"
import {
  BaseResponse,
  parseErrorContainer,
  parseItemsResponse,
  updateItemsResponse,
  updateSingleResponse,
  type ErrorContainer,
  type ItemsResponse,
  type SingleResponse,
} from "./response"
import type TwSession from "./session"
import type { SessionErrorHandler } from "./session-error-handler"
import type { ArrayMap } from "./util/collection"
import {
  JsonBuilder,
  JsonHelper,
  type JsonKeyExtractor,
  type JsonMap,
} from "./util/json"

export const JSON_MIME_TYPE = "application/json"
export const MULTIPART_FORM_MIME_TYPE = "multipart/form-data"
export const CONTENT_TYPE = "Content-Type"
export const AUTHORIZATION = "Authorization"
export const OTP_HEADER = "x-tastyworks-otp"
export const CASTLE_TOKEN_HEADER = "X-Castle-Request-Token"
export const CSRF_HEADER = "X-Tastyworks-CSRF"
// CSRF defense is the mere presence of this custom header: browsers can't set it
// on forged cross-origin requests, so the value itself is irrelevant.
export const CSRF_HEADER_VALUE = "1"
export const DEVICE_CHALLENGE_TOKEN_HEADER = "X-Tastyworks-Challenge-Token"
export const ATTEMPTS_REMAINING_HEADER = "x-tastyworks-login-attempts-remaining"
const HTTP_STATUS_NO_CONTENT = 204
const HTTP_INVALID_SESSION = 401
const HTTP_FORBIDDEN = 403
const HTTP_TOO_MANY_REQUESTS = 429
const HTTP_INTERNAL_SERVER_ERROR = 500

export type HttpMethod = "DELETE" | "GET" | "PATCH" | "POST" | "PUT"

export type Transport = "bearer" | "cookie-csrf"

export const INVALID_SESSION_MESSAGE = "Invalid Session"
export const INTERNAL_SERVER_ERROR_MESSAGE = "Internal Server Error"
export const TWO_FACTOR_AUTH_ERROR = "Two Factor Auth Required"
export const HTTP_TOO_MANY_REQUESTS_MESSAGE = "Too Many Requests"
export const INVALID_CREDENTIALS_CODE = "invalid_credentials"
export const INVALID_SECURITY_QUESTION_CODE = "invalid_security_question_answer"
export const OTP_RATE_LIMIT_EXCEEDED_CODE = "otp_rate_limit_exceeded"
const DEVICE_CHALLENGE_ERROR_CODE = "device_challenge_required"

export interface FetchOption {
  body?: FormData | string
}

export class InvalidSessionError extends Error {
  constructor(
    readonly method: HttpMethod,
    readonly url: string,
    readonly response: Response,
    readonly jsonHelper?: JsonHelper
  ) {
    super(INVALID_SESSION_MESSAGE)

    // https://github.com/Microsoft/TypeScript-wiki/blob/master/Breaking-Changes.md#extending-built-ins-like-error-array-and-map-may-no-longer-work
    // Set the prototype explicitly.
    Object.setPrototypeOf(this, InvalidSessionError.prototype)
  }
}

export class TwoFactorAuthError extends Error {
  constructor(
    readonly method: HttpMethod,
    readonly url: string,
    readonly response: Response,
    readonly jsonHelper: JsonHelper,
    readonly attemptsRemaining: string | null
  ) {
    super(TWO_FACTOR_AUTH_ERROR)

    // https://github.com/Microsoft/TypeScript-wiki/blob/master/Breaking-Changes.md#extending-built-ins-like-error-array-and-map-may-no-longer-work
    // Set the prototype explicitly.
    Object.setPrototypeOf(this, TwoFactorAuthError.prototype)
  }

  get twoFactorType(): TWO_FACTOR_TYPE | null {
    return twoFactorType(this.response)
  }

  get hasLockedAccount() {
    return this.attemptsRemaining === null || this.attemptsRemaining === "0"
  }
}

export class DeviceChallengeError extends Error {
  constructor(
    readonly code: string,
    readonly message: string,
    readonly deviceChallengeToken: string,
    readonly response: Response
  ) {
    super(message)
  }
}

export class TooManyRequestsError extends Error {
  constructor(
    readonly method: HttpMethod,
    readonly url: string,
    readonly response: Response,
    message?: string
  ) {
    // oxlint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    super(message || HTTP_TOO_MANY_REQUESTS_MESSAGE)
    Object.setPrototypeOf(this, TooManyRequestsError.prototype)
  }

  get twoFactorType(): TWO_FACTOR_TYPE | null {
    return twoFactorType(this.response)
  }
}

export class InternalServerError extends Error {
  constructor(
    readonly method: HttpMethod,
    readonly url: string,
    readonly response: Response
  ) {
    super(INTERNAL_SERVER_ERROR_MESSAGE)

    // NOTE [DK]:
    // https://github.com/Microsoft/TypeScript-wiki/blob/master/Breaking-Changes.md#extending-built-ins-like-error-array-and-map-may-no-longer-work
    // Set the prototype explicitly.
    Object.setPrototypeOf(this, InternalServerError.prototype)
  }
}

export class InvalidAnswerError extends Error {
  constructor(
    readonly message: string,
    readonly attemptsRemaining: string | null
  ) {
    super(message)
  }

  get hasLockedAccount() {
    return this.attemptsRemaining === null || this.attemptsRemaining === "0"
  }
}

function isOTPStatusCode(status: number): boolean {
  return status === HTTP_INVALID_SESSION || status === HTTP_FORBIDDEN
}

export function isOTPResponse(response: Response): boolean {
  const header = response.headers.get(OTP_HEADER)
  return (
    isOTPStatusCode(response.status) &&
    !isNil(header) &&
    startsWith(header, "required")
  )
}

export function isInvalidCredentialsResponse(error: ErrorContainer) {
  return error.code === INVALID_CREDENTIALS_CODE
}

export function isInvalidSecurityQuestionResponse(
  error: ErrorContainer
): boolean {
  return error.code === INVALID_SECURITY_QUESTION_CODE
}

export function isInvalidAnswerError(
  error: unknown
): error is InvalidAnswerError {
  return error instanceof InvalidAnswerError
}

export function isDeviceChallengeResponse(
  response: Response,
  errorCode: string
) {
  return (
    response.status === HTTP_FORBIDDEN &&
    errorCode === DEVICE_CHALLENGE_ERROR_CODE
  )
}

export function isDeviceChallengeError(
  error: unknown
): error is DeviceChallengeError {
  return (
    error instanceof DeviceChallengeError &&
    isDeviceChallengeResponse(error.response, error.code)
  )
}

export function isOTPError(error: unknown): error is TwoFactorAuthError {
  return error instanceof TwoFactorAuthError && isOTPResponse(error.response)
}

export function isTooManyRequestsResponse(response: Response): boolean {
  return response.status === HTTP_TOO_MANY_REQUESTS
}

export function isTooManyRequestsError(
  error: unknown
): error is TooManyRequestsError {
  return error instanceof TooManyRequestsError
}

export function twoFactorType(response: Response): TWO_FACTOR_TYPE | null {
  const otpHeader = response.headers.get(OTP_HEADER)
  if (isNil(otpHeader)) {
    return null
  }

  const twoFactorMethod = last(otpHeader.split(";"))
  if (isNil(twoFactorMethod)) {
    return null
  }

  switch (twoFactorMethod.trim()) {
    case TWO_FACTOR_TYPE.AUTHENTICATOR:
      return TWO_FACTOR_TYPE.AUTHENTICATOR
    case TWO_FACTOR_TYPE.SMS:
      return TWO_FACTOR_TYPE.SMS
    default:
      return null
  }
}

export async function toJsonHelper(response: Response) {
  if (response.status === HTTP_STATUS_NO_CONTENT) {
    // 204 responses don't have a JSON body so passing through an empty object
    return new JsonHelper({})
  }

  const contentType = response.headers.get(CONTENT_TYPE)

  if (!contentType?.startsWith(JSON_MIME_TYPE)) {
    return new JsonHelper({})
  }

  const json = (await response.json()) as JsonMap
  return new JsonHelper(json)
}

type HttpAction = (
  path: string,
  body: any,
  params?: Params
) => Promise<JsonHelper>

const actionOnSingleResource = async <T extends RestResource>(
  httpAction: HttpAction,
  target: T,
  deSer: DeSer<T>
): Promise<SingleResponse<T>> => {
  const builder = new JsonBuilder()

  deSer.serialize(target, builder)

  const resourceParams = {
    id: target.id,
  }

  const updater = (_target: T, helper: JsonHelper) => {
    deSer.update(_target, helper)
  }

  return httpAction(":id", builder.json, resourceParams).then((helper) =>
    updateSingleResponse(helper, target, updater)
  )
}

export default class HttpClient {
  public readonly baseUrl: UrlPathBuilder
  public readonly session: TwSession
  public readonly defaultParams: object

  public constructor(
    private readonly logger: Logger,
    readonly fetcher: typeof fetch,
    $baseUrl: string,
    $twSession: TwSession,
    readonly sessionErrorHandler: SessionErrorHandler,
    $urlPathBuilder?: UrlPathBuilder,
    defaultParams = {},
    readonly transport: Transport | (() => Transport) = "bearer"
  ) {
    this.defaultParams = defaultParams
    this.baseUrl = $urlPathBuilder
      ? $urlPathBuilder.nested($baseUrl, this.defaultParams)
      : new UrlPathBuilder($baseUrl, "", this.defaultParams)
    this.session = $twSession
  }

  get currentTransport(): Transport {
    return typeof this.transport === "function"
      ? this.transport()
      : this.transport
  }

  nested = (path: string, defaultParams = {}): HttpClient =>
    new HttpClient(
      this.logger,
      this.fetcher,
      path,
      this.session,
      this.sessionErrorHandler,
      this.baseUrl,
      merge(defaultParams, this.defaultParams),
      this.transport
    )

  private readonly fetchWithErrorHandler = async (
    url: string,
    request: RequestInit
  ) => {
    try {
      /* cors is the default mode for browsers,
      but since svelte-kit may run in a node environment,
      we need to set it explicitly in order to support that framework */
      request.mode = "cors"
      return await this.fetcher(url, request)
    } catch (error) {
      this.sessionErrorHandler.onHttpNetworkError(url, request, error)
      throw error
    }
  }

  fetch = async (
    url: string,
    method: HttpMethod,
    body?: FormData | string,
    headers: HeadersInit = { Accept: JSON_MIME_TYPE }
  ): Promise<JsonHelper> => {
    const transport = this.currentTransport
    let requestHeaders: HeadersInit = {}
    if (transport === "cookie-csrf") {
      // Cookie carries auth; never attach the bearer token on the canonical host.
      requestHeaders[CSRF_HEADER] = CSRF_HEADER_VALUE
    } else {
      const { sessionToken } = this.session
      if (!isEmpty(sessionToken)) {
        requestHeaders[AUTHORIZATION] = sessionToken
      }
    }
    const castleHeaders = await this.session.getCastleHeaders()
    if (!isEmpty(castleHeaders)) {
      requestHeaders = { ...requestHeaders, ...castleHeaders }
    }
    if (this.session.oneTimePassword.length > 0) {
      requestHeaders[OTP_HEADER] = this.session.oneTimePassword
    }

    if (
      typeof body !== "string" &&
      !isNil(body) &&
      [...body.values()].some((v) => v instanceof File)
    ) {
      // Contains at least one file; ensure content-type header is
      // unset so user-agent sets it with boundary.
      // oxlint-disable-next-line @typescript-eslint/no-dynamic-delete
      delete requestHeaders[CONTENT_TYPE]
    } else {
      // Defaults to application/json without files. Can be overriden by `headers` passed in
      requestHeaders[CONTENT_TYPE] = JSON_MIME_TYPE
    }

    Object.assign(requestHeaders, headers)

    const request: RequestInit = {
      body,

      headers: requestHeaders,
      method,
    }
    if (transport === "cookie-csrf") {
      request.credentials = "include"
    }

    const response = await this.fetchWithErrorHandler(url, request)

    if (isOTPResponse(response)) {
      const attemptsRemaining = response.headers.get(ATTEMPTS_REMAINING_HEADER)
      this.sessionErrorHandler.onOTPRequired(response)
      const helper = await toJsonHelper(response)

      throw new TwoFactorAuthError(
        method,
        url,
        response,
        helper,
        attemptsRemaining
      )
    } else if (response.status === HTTP_INVALID_SESSION) {
      const baseResponse = new BaseResponse()
      const helper = await toJsonHelper(response)
      parseErrorContainer(helper, baseResponse)
      this.sessionErrorHandler.onInvalidSession(baseResponse)

      if (baseResponse.errorContainer.code !== INVALID_CREDENTIALS_CODE) {
        throw new InvalidSessionError(method, url, response, helper)
      }
      return helper
    } else if (response.status >= HTTP_INTERNAL_SERVER_ERROR) {
      throw new InternalServerError(method, url, response)
    }

    return toJsonHelper(response)
  }

  get = async (
    path = "",
    params?: Params,
    headers?: HeadersInit
  ): Promise<JsonHelper> => {
    const url = this.baseUrl.toUrl(path, params)
    return this.fetch(url, "GET", undefined, headers)
  }

  patch = async (
    path = "",
    body: any,
    params?: Params,
    headers?: HeadersInit
  ): Promise<JsonHelper> => {
    const url = this.baseUrl.toUrl(path, params)
    return this.fetch(url, "PATCH", JSON.stringify(body), headers)
  }

  put = async (path = "", body: any, params?: Params): Promise<JsonHelper> => {
    const url = this.baseUrl.toUrl(path, params)
    return this.fetch(url, "PUT", JSON.stringify(body))
  }

  postJson = async (
    path = "",
    body: any,
    pathParams?: Params,
    headers?: HeadersInit
  ): Promise<JsonHelper> => {
    const url = this.baseUrl.toUrl(path, pathParams)
    return this.fetch(url, "POST", JSON.stringify(body), headers)
  }

  postForm = async (
    path: string,
    formParams: Params,
    pathParam?: Params
  ): Promise<JsonHelper> => {
    const url = this.baseUrl.toUrl(path, pathParam)
    return this.fetch(url, "POST", toFormData(formParams), {
      [CONTENT_TYPE]: MULTIPART_FORM_MIME_TYPE,
    })
  }

  postFiles = async (
    path: string,
    formParams: Params,
    fileParams: FileParams,
    pathParam?: Params
  ): Promise<JsonHelper> => {
    const url = this.baseUrl.toUrl(path, pathParam)
    const formData = toFormData(formParams)
    for (const [key, value] of Object.entries(fileParams)) {
      formData.append(key, value, value.name)
    }
    return this.fetch(url, "POST", formData, {})
  }

  doDelete = async (
    path = "",
    pathParam?: Params,
    headers?: HeadersInit,
    body?: any
  ): Promise<JsonHelper> => {
    const url = this.baseUrl.toUrl(path, pathParam)
    return this.fetch(
      url,
      "DELETE",
      // oxlint-disable-next-line eqeqeq
      body == null ? undefined : JSON.stringify(body),
      headers
    )
  }

  index = async <K, V>(
    target: ArrayMap<K, V>,
    updater: Updater<V>,
    jsonKeyExtractor: JsonKeyExtractor<K>,
    path?: string,
    params?: Params
  ): Promise<ItemsResponse<V>> =>
    this.get(path, params).then((helper) =>
      updateItemsResponse(helper, target, updater, jsonKeyExtractor)
    )

  indexSimple = async <T>(
    parser: Parser<T>,
    path?: string,
    params?: Params,
    headers?: HeadersInit
  ): Promise<ItemsResponse<T>> =>
    this.get(path, params, headers).then((helper) =>
      parseItemsResponse(helper, parser)
    )

  show = async <T>(
    target: T,
    updater: Updater<T>,
    path?: string,
    params?: Params,
    headers?: HeadersInit
  ): Promise<SingleResponse<T>> =>
    this.get(path, params, headers).then((helper) =>
      updateSingleResponse(helper, target, updater)
    )

  create = async <T>(
    target: T,
    serializer: Serializer<T>,
    updater: Updater<T>,
    path?: string,
    pathParams?: Params,
    headers?: HeadersInit
  ): Promise<SingleResponse<T>> => {
    const builder = new JsonBuilder()

    serializer(target, builder)

    return this.postJson(path, builder.json, pathParams, headers).then(
      (helper) => updateSingleResponse(helper, target, updater)
    )
  }

  update = async <T>(
    target: T,
    serializer: Serializer<T>,
    updater: Updater<T>,
    path?: string,
    pathParams?: Params
  ): Promise<SingleResponse<T>> => {
    const builder = new JsonBuilder()

    serializer(target, builder)

    return this.patch(path, builder.json, pathParams).then((helper) =>
      updateSingleResponse(helper, target, updater)
    )
  }

  updateResource = async <T extends RestResource>(
    target: T,
    deSer: DeSer<T>
  ): Promise<SingleResponse<T>> =>
    actionOnSingleResource(this.patch, target, deSer)

  save = async <T>(
    target: T,
    serializer: Serializer<T>,
    updater: Updater<T>,
    path?: string
  ): Promise<SingleResponse<T>> => {
    const builder = new JsonBuilder()

    serializer(target, builder)

    return this.put(path, builder.json).then((helper) =>
      updateSingleResponse(helper, target, updater)
    )
  }

  saveResource = async <T extends RestResource>(
    target: T,
    deSer: DeSer<T>
  ): Promise<SingleResponse<T>> =>
    actionOnSingleResource(this.put, target, deSer)

  destroy = async (
    path?: string,
    pathParams?: Params,
    headers?: HeadersInit
  ): Promise<BaseResponse> => {
    const helper = await this.doDelete(path, pathParams, headers)

    const response = new BaseResponse()
    parseErrorContainer(helper, response)

    return response
  }
}
